import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { FadeUp, SectionHeading } from "@/components/anim";
import { FALLBACK_IMAGE } from "@/lib/site";

const KERRY_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Kerry White",
  jobTitle: "Principal & Choreographer",
  worksFor: { "@type": "LocalBusiness", name: "Kerry's" },
  alumniOf: { "@type": "EducationalOrganization", name: "Laine Theatre Arts" },
  description:
    "Kerry White is the principal of Kerry's, a performing arts school in Whitstable, Kent. Laine Theatre Arts graduate, former featured dancer in The Michael Jackson Experience and professional pantomime choreographer.",
};

const TIMELINE = [
  {
    era: "The early years",
    title: "A Whitstable dancer, through and through",
    text: "Kerry trained at the Deborah Capon School here in Whitstable for all of her younger years — the same seaside town where she now teaches the next generation.",
  },
  {
    era: "Competition years",
    title: "All England champion",
    text: "Competing in festivals and major competitions as a child, Kerry took First at the All England for National Dance, First at the Janet Cram Awards for Modern, and Second at the Marjorie Davies Star Tap Awards.",
  },
  {
    era: "Training",
    title: "Laine Theatre Arts",
    text: "Kerry completed her Diploma in Dance and Musical Theatre at the world-famous Laine Theatre Arts, starring in shows including Fosse, Scrooge, Bye Bye Birdie and My Fair Lady.",
  },
  {
    era: "On tour",
    title: "The Michael Jackson Experience",
    text: "As a featured dancer, Kerry toured across the UK and to Holland with The Michael Jackson Experience — living the performing life she now prepares her students for.",
  },
  {
    era: "Choreography",
    title: "Panto, stars and the Winter Gardens",
    text: "Kerry has choreographed many professional shows, including Oliver! at the Winter Gardens and pantomimes across the country — working with Barry Humphries (Dame Edna), Stephen Mulhern, Anita Dobson, Mr Bloom (CBeebies) and Sherrie Hewson.",
  },
  {
    era: "Today",
    title: "Kerry's — the next chapter",
    text: "With 17 years of teaching behind her and students who've gone on to top performing arts colleges, Kerry leads her school with the same promise as day one: brilliant training, warm hearts, and a 100% ISTD exam pass record.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Kerry White"
        description={"Meet Kerry White — Laine Theatre Arts graduate, former Michael Jackson Experience featured dancer, professional pantomime choreographer and principal of Kerry's performing arts school in Whitstable."}
        path="/about"
        jsonLd={KERRY_JSONLD}
      />
      <section className="grain border-b-[0.5px] border-foreground/30 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <FadeUp>
              <Badge variant="outline" className="rounded-none border-[0.5px] border-foreground bg-card font-bold uppercase tracking-[0.15em] text-[11px] px-4 py-1.5 mb-6">
                Principal &middot; Dancer &middot; Choreographer
              </Badge>
              <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6.5rem] leading-[0.88]">
                Meet
                <br />
                <span className="text-[hsl(var(--kerrys-sea))]">Kerry White</span>
              </h1>
              <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
                Dancer, teacher, choreographer &mdash; and the heart of Kerry&rsquo;s (formerly Newcomb Theatre Arts).
                For 17 years, Kerry has trained Whitstable&rsquo;s young performers &mdash; with many
                going on to the country&rsquo;s top colleges and professional stages.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="about-classes-button"
                >
                  <Link to="/classes">
                    See the classes <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="about-book-button"
                >
                  <Link to="/book">Book a class</Link>
                </Button>
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-5 relative lg:-mr-10">
            <FadeUp delay={0.12}>
              <div className="relative">
                <img
                  src="/images/kerry-headshot.webp"
                  alt="Kerry White, principal of Kerry's, smiling"
                  className="w-full h-[340px] sm:h-[440px] lg:h-[540px] object-cover border-[0.5px] border-foreground"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMAGE;
                  }}
                />
                <div className="absolute -bottom-7 -left-4 sm:-left-8 bg-[hsl(var(--kerrys-sun))] text-foreground px-6 py-4 rotate-2 border-[0.5px] border-foreground">
                  <p className="font-display text-2xl leading-none uppercase">Laine trained</p>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] mt-1 text-foreground/70">Dance &amp; Musical Theatre</p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]" data-testid="about-timeline-section">
        <SectionHeading eyebrow="Her story" title="Whitstable, the stage, and back again" />
        <div className="mt-20 relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-foreground/30" aria-hidden="true" />
          <div className="space-y-14 md:space-y-0">
            {TIMELINE.map((item, i) => (
              <FadeUp key={item.title} delay={i * 0.04}>
                <div className={`md:grid md:grid-cols-2 md:gap-16 items-start ${i > 0 ? "md:-mt-6" : ""}`} data-testid="about-timeline-card">
                  <div className={`${i % 2 === 0 ? "md:pr-16 md:text-right" : "md:col-start-2 md:pl-16"} border-t-[0.5px] border-foreground pt-6 md:pb-24`}>
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-[hsl(var(--kerrys-coral))]">{item.era}</p>
                    <h3 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95] mt-3">{item.title}</h3>
                    <p className={`text-muted-foreground mt-4 leading-relaxed ${i % 2 === 0 ? "md:ml-auto" : ""} max-w-md`}>{item.text}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[hsl(var(--kerrys-ink))] text-background grain border-t-[0.5px] border-foreground">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
          <FadeUp>
            <p className="font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
              &ldquo;The shows matter. But what I really care about is each child reaching their own best &mdash; in a room
              that feels like <span className="text-[hsl(var(--kerrys-sun))]">home</span>.&rdquo;
            </p>
            <p className="mt-8 text-sm font-bold uppercase tracking-[0.3em] text-background/60">Kerry White &middot; Principal</p>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
